import { useMemo, useState } from 'react';
import { D, fmtAmt, type Account, type CcyCode, type Money } from '@wave/shared';
import { useChats } from '../api/chats';
import { hasRight, useAuthStore } from '../auth/store';
import { useSyncTelegramChats } from '../api/mutations';
import { ChatSyncConfigModal } from '../components/modals/ChatSyncConfigModal';
import { IconSync, IconSettings } from '../components/icons';
import {
  btnGhostSm,
  btnPrimary,
  pageContainer,
  pageHeader,
  pageSubtitle,
  pageTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
  tableWrapper,
} from '../lib/styles';
import { ChatEditModal } from '../components/modals/ChatEditModal';

type FilterKind = 'all' | 'client' | 'executor' | 'inactive';

export function AdminChatsPage() {
  const { data: chats, isLoading, error } = useChats();
  const [filter, setFilter] = useState<FilterKind>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Account | null>(null);
  const [creating, setCreating] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const sync = useSyncTelegramChats();
  // Синхронизация из внешнего API и настройка источника — только админ (refs.write).
  // Создание/редактирование контрагентов доступно и оператору/менеджеру (accounts.manage).
  const canManageRefs = hasRight(useAuthStore((s) => s.rights), 'refs.write');

  async function doSync() {
    setSyncMsg(null);
    try {
      const r = await sync.mutateAsync();
      setSyncMsg(
        r.status === 'ok'
          ? `Синхронизировано: добавлено ${r.added}, обновлено ${r.updated} (всего в источнике ${r.total})`
          : `Не удалось: ${r.error ?? 'неизвестная ошибка'}`,
      );
    } catch (err) {
      setSyncMsg(`Не удалось: ${(err as Error).message}`);
    }
  }

  const counts = useMemo(() => {
    const all = chats?.length ?? 0;
    let client = 0,
      executor = 0,
      inactive = 0;
    for (const a of chats ?? []) {
      if (!a.isActive) inactive++;
      if (a.kind === 'client') client++;
      else if (a.kind === 'executor') executor++;
    }
    return { all, client, executor, inactive };
  }, [chats]);

  const filtered = useMemo(() => {
    if (!chats) return [];
    const q = search.trim().toLowerCase();
    return chats.filter((a) => {
      if (filter === 'inactive' && a.isActive) return false;
      if (filter === 'client' && a.kind !== 'client') return false;
      if (filter === 'executor' && a.kind !== 'executor') return false;
      if (q) {
        const hay = [
          a.code,
          a.displayName ?? '',
          a.telegramChatTitle ?? '',
          a.telegramChatId ?? '',
          a.notes ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [chats, filter, search]);

  const filtersList: { key: FilterKind; label: string; count: number }[] = [
    { key: 'all', label: 'Все', count: counts.all },
    { key: 'client', label: 'Клиенты', count: counts.client },
    { key: 'executor', label: 'Исполнители', count: counts.executor },
    { key: 'inactive', label: 'Неактивные', count: counts.inactive },
  ];

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Чаты и контрагенты</h1>
          <p className={pageSubtitle}>
            Привязки Telegram-чатов к учётным записям. Клиенты и исполнители, их балансы, идентификаторы.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageRefs && (
            <>
              <button
                onClick={() => void doSync()}
                disabled={sync.isPending}
                className={btnGhostSm}
                title="Подтянуть список чатов из внешнего API в контрагенты"
              >
                <IconSync className={sync.isPending ? 'animate-spin' : undefined} />
                Синхронизировать
              </button>
              <button
                onClick={() => setConfigOpen(true)}
                className={btnGhostSm}
                title="Источник синхронизации (URL и токен внешнего API)"
              >
                <IconSettings />
                Источник
              </button>
            </>
          )}
          <button onClick={() => setCreating(true)} className={btnPrimary}>
            + Новый чат
          </button>
        </div>
      </div>

      {syncMsg && <div className="mb-3 text-xs text-text2">{syncMsg}</div>}
      {configOpen && <ChatSyncConfigModal onClose={() => setConfigOpen(false)} />}

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
          {filtersList.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex h-8 items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors duration-150 ${
                  active
                    ? 'bg-accent text-white shadow-xs'
                    : 'text-text2 hover:bg-bg2 hover:text-text'
                }`}
              >
                {f.label}
                <span
                  className={`rounded px-1 font-mono text-[10px] tabular-nums ${
                    active ? 'bg-white/20 text-white' : 'bg-bg3 text-text2'
                  }`}
                >
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <SearchIcon />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск по коду, имени, чату…"
            className="block h-9 w-full rounded-md border border-border bg-bg pl-8 pr-3 text-sm text-text outline-none transition-colors duration-150 hover:border-border-strong focus:border-accent focus:shadow-focus placeholder:text-text-subtle"
          />
        </div>
        <div className="ml-auto text-xs text-text2">
          <span className="font-mono font-medium text-text">{filtered.length}</span> из{' '}
          <span className="font-mono">{counts.all}</span>
        </div>
      </div>

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {chats && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-bg2 px-6 py-12 text-center">
          <div className="text-sm font-medium text-text">Ничего не найдено</div>
          <div className="mt-1 text-xs text-text2">Попробуйте изменить фильтры или сбросить поиск.</div>
        </div>
      )}

      {filtered.length > 0 && (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Код</th>
                <th className={tableHeadCell}>Роль</th>
                <th className={tableHeadCell}>Имя / название</th>
                <th className={tableHeadCell}>Telegram</th>
                <th className={tableHeadCell}>Стартовые балансы</th>
                <th className={tableHeadCellRight}>Заявок</th>
                <th className={tableHeadCell}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.code}
                  className={`row-clickable ${tableRow} ${!a.isActive ? 'opacity-60' : ''}`}
                  onClick={() => setEditing(a)}
                >
                  <td className={`${tableCell} font-mono text-sm font-semibold text-accent`}>
                    {a.code}
                  </td>
                  <td className={tableCell}>
                    <RoleTag kind={a.kind} />
                  </td>
                  <td className={tableCell}>
                    {a.displayName ? (
                      <span className="font-medium text-text">{a.displayName}</span>
                    ) : (
                      <span className="text-text-subtle">—</span>
                    )}
                  </td>
                  <td className={tableCell}>
                    {a.telegramChatId || a.telegramChatTitle ? (
                      <div>
                        {a.telegramChatTitle && (
                          <div className="text-sm text-text">{a.telegramChatTitle}</div>
                        )}
                        {a.telegramChatId && (
                          <div className="font-mono text-[11px] text-text2">{a.telegramChatId}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-text-subtle">не привязан</span>
                    )}
                  </td>
                  <td className={tableCell}>
                    <BalancePreview balances={a.initialBalances ?? {}} />
                  </td>
                  <td className={`${tableCell} text-right font-mono tabular-nums text-text2`}>
                    {a.ordersCount ?? 0}
                  </td>
                  <td className={tableCell}>
                    {a.isActive ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                        <Dot color="success" /> активен
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-text2">
                        <Dot color="muted" /> неактивен
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ChatEditModal
          mode="create"
          onClose={() => setCreating(false)}
          onDone={() => setCreating(false)}
        />
      )}
      {editing && (
        <ChatEditModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onDone={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function RoleTag({ kind }: { kind: 'client' | 'executor' }) {
  const cls =
    kind === 'client'
      ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
      : 'border-[var(--green-border)] bg-[var(--green-bg)] text-success';
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>
      {kind === 'client' ? 'Клиент' : 'Исполнитель'}
    </span>
  );
}

function Dot({ color }: { color: 'success' | 'muted' }) {
  const cls = color === 'success' ? 'bg-success' : 'bg-text-subtle';
  return <span className={`inline-block h-1.5 w-1.5 rounded-full ${cls}`} />;
}

function BalancePreview({ balances }: { balances: Record<string, Money> }) {
  const entries = Object.entries(balances).filter(([, v]) => v && !D(v).isZero());
  if (entries.length === 0) return <span className="text-text-subtle">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 4).map(([ccy, val]) => {
        const isPos = D(val).isPositive();
        const cls = isPos
          ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-success'
          : 'border-[var(--red-border)] bg-[var(--red-bg)] text-danger';
        return (
          <span
            key={ccy}
            className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums ${cls}`}
          >
            {fmtAmt(val, ccy as CcyCode)}
          </span>
        );
      })}
      {entries.length > 4 && (
        <span className="inline-flex items-center text-[11px] text-text2">+{entries.length - 4}</span>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
